import { NavLink, useNavigate, useLocation } from "react-router-dom";
import { Search, Map, MessageCircle, User, Plus, LayoutDashboard } from "lucide-react";
import { motion } from "framer-motion";
import { useAuth } from "@/hooks/useAuth";
import { useNavLabel } from "@/components/NavLabel";
import { cn } from "@/lib/utils";

/**
 * Bottom navigation — only rendered for authenticated users on mobile.
 * Centered "+" button is the primary CTA (start a new trip).
 * Routes that should NOT display it (full-screen flows) are filtered out.
 */
const HIDDEN_ROUTES = ["/itinerary", "/auth", "/reset-password", "/shared"];

const MobileBottomNav = () => {
  const { user, plan, isAdmin } = useAuth();
  const tt = useNavLabel();
  const navigate = useNavigate();
  const location = useLocation();

  if (!user) return null;
  if (HIDDEN_ROUTES.some((p) => location.pathname.startsWith(p))) return null;

  const items: Array<{ path: string; icon: React.ElementType; label: string }> = [
    { path: "/search", icon: Search, label: tt("nav.search", "Recherche") },
    { path: "/my-trips", icon: Map, label: tt("nav.myTrips", "Voyages") },
    ...(plan !== "free"
      ? [{ path: "/discussion", icon: MessageCircle, label: tt("nav.discussion", "Discussion") }]
      : []),
    ...(isAdmin
      ? [{ path: "/admin", icon: LayoutDashboard, label: "Admin" }]
      : []),
    { path: "/settings", icon: User, label: tt("nav.profile", "Profil") },
  ];

  return (
    <>
      {/* Spacer so page content isn't hidden under the bar */}
      <div className="md:hidden h-20" aria-hidden />
      <motion.nav
        initial={{ y: 80 }}
        animate={{ y: 0 }}
        transition={{ type: "spring", stiffness: 300, damping: 30 }}
        className="md:hidden fixed bottom-0 inset-x-0 z-40 border-t border-border bg-background/95 backdrop-blur-md pb-[env(safe-area-inset-bottom)]"
      >
        <div className="grid h-16 relative" style={{ gridTemplateColumns: `repeat(${items.length + 1}, minmax(0, 1fr))` }}>
          {items.slice(0, 2).map((it) => (
            <NavItem key={it.path} {...it} />
          ))}
          <div className="flex items-center justify-center">
            <motion.button
              whileTap={{ scale: 0.9 }}
              whileHover={{ scale: 1.05 }}
              onClick={() => navigate("/itinerary")}
              aria-label={tt("nav.newTrip", "Nouveau voyage")}
              className="-mt-6 w-14 h-14 rounded-full gradient-primary text-white shadow-lg flex items-center justify-center transition-shadow hover:shadow-xl"
            >
              <Plus className="w-6 h-6" />
            </motion.button>
          </div>
          {items.slice(2).map((it) => (
            <NavItem key={it.path} {...it} />
          ))}
        </div>
      </motion.nav>
    </>
  );
};

const NavItem = ({
  path,
  icon: Icon,
  label,
}: {
  path: string;
  icon: React.ElementType;
  label: string;
}) => (
  <NavLink
    to={path}
    className={({ isActive }) =>
      cn(
        "relative flex flex-col items-center justify-center gap-0.5 text-[10px] font-medium transition-colors min-h-[44px]",
        isActive ? "text-primary" : "text-muted-foreground hover:text-foreground",
      )
    }
  >
    {({ isActive }) => (
      <>
        {isActive && (
          <motion.span
            layoutId="bottom-nav-indicator"
            transition={{ type: "spring", stiffness: 400, damping: 32 }}
            className="absolute top-0 h-0.5 w-8 rounded-full bg-primary"
          />
        )}
        <motion.span
          animate={{ scale: isActive ? 1.12 : 1, y: isActive ? -1 : 0 }}
          transition={{ type: "spring", stiffness: 400, damping: 25 }}
        >
          <Icon className="w-5 h-5" />
        </motion.span>
        <span className="truncate max-w-[60px]">{label}</span>
      </>
    )}
  </NavLink>
);

export default MobileBottomNav;
